import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * A custom React hook that provides debounced function execution with loading state.
 * The hook ensures that the callback is not called more frequently than the specified delay.
 * 
 * @param {Function} callback - The async function to be debounced
 * @param {number} [delay=300] - The debounce delay in milliseconds (default: 300ms)
 * @returns {[Function, boolean]} - A tuple containing:
 *   - debouncedFn: The debounced version of the callback
 *   - isLoading: Boolean indicating if the debounced function is currently executing
 * 
 * @example
 * const [search, isLoading] = useDebounce(async (query) => {
 *   const results = await fetchResults(query);
 *   setResults(results);
 * }, 500);
 * 
 * // In your component:
 * <input onChange={(e) => search(e.target.value)} />
 * {isLoading && <div>Loading...</div>}
 */
function useDebounce(callback, delay = 300) {
  // Store the timer reference for cleanup
  const timerRef = useRef(null);
  // Track loading state
  const [isLoading, setIsLoading] = useState(false);
  // Track mounted state to prevent state updates after unmount
  const isMounted = useRef(true);

  // Set up cleanup for when component unmounts
  useEffect(() => {
    return () => {
      isMounted.current = false;
      // Clear any pending timers on unmount
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  /**
   * Creates a debounced function that delays invoking the callback
   * until after the delay has elapsed since the last time it was invoked.
   */
  const debouncedFunction = useCallback(
    (...args) => {
      // Clear any existing timer to prevent multiple executions
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      // Set loading state
      setIsLoading(true);

      // Create a new timer
      timerRef.current = setTimeout(async () => {
        try {
          // Execute the callback with provided arguments
          await callback(...args);
        } catch (error) {
          // Log errors but don't break the application flow
          console.error('Error in debounced function:', error);
          // Re-throw the error to allow error handling by the caller
          throw error;
        } finally {
          // Only update state if component is still mounted
          if (isMounted.current) {
            setIsLoading(false);
          }
        }
      }, delay);
    },
    [callback, delay] // Only recreate if these dependencies change
  );

  return [debouncedFunction, isLoading];
}

export default useDebounce;